import Loading from '#components/a/Loading'
import Text from '#components/a/Text'
import Pagination from '#components/m/Pagination'
import {
  registerScrollRestoration,
  restoreScrollPosition,
  unregisterScrollRestoration,
} from '#helpers/scrollRestorationHelper'
import Api from '#services/api'
import {calculateAzubiNachweisStatus} from '#tools/azubiBadgeStatusCalculator'
import toolAusbildungsNachweisSort from 'ag-common/tools/ausbildungsNachweisSort'
import toolGetFormattedDateRange from 'ag-common/tools/nachweisDateRangeFormatter'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'
import actions from 'redux-auto'
import NachweisListeAusbilderPerAzubi from './NachweisListeAusbilderPerAzubi'
import NachweisListeHeaderAusbilder from './NachweisListeHeaderAusbilder'

function NachweisListeAusbilder({azubiId}) {
  const [azubis, setAzubis] = useState(undefined)
  const [visibleAzubis, setVisibleAzubis] = useState([])
  const [hasAzubisWithModule, setHasAzubisWithModule] = useState(true)
  const location = useLocation()

  const filter = useSelector(state => state.filter)
  const selectedAzubiIdsFromGlobalFilter = filter ? filter.matchingAzubiIds : []

  const filteredAzubis = azubis?.filter(azubi => selectedAzubiIdsFromGlobalFilter.includes(azubi.userId))

  const fetchNachweise = async () => {
    try {
      let result = await Api.readNachweisList(azubiId)

      let orderedResult = result.map(async azubi => {
        let userInfo = await Api.getUserById(azubi.userId)
        return {
          ...azubi,
          nachweisStatus: calculateAzubiNachweisStatus({
            ...azubi,
            ausbildungsstart: userInfo.ausbildungsstart,
            ausbildungsnachweisTyp: userInfo.ausbildungsnachweisTyp,
          }),
          ausbildungsnachweise: azubi.ausbildungsnachweise.map(nachweis => {
            return {
              ...nachweis,
              dateRange: toolGetFormattedDateRange.getFormattedDateRange(nachweis, userInfo),
            }
          }),
        }
      })

      let awaitedResult = await Promise.all(orderedResult)
      setAzubis(awaitedResult)

      if (result.length === 0) {
        setHasAzubisWithModule(await Api.hasAzubisWithNachweisModul())
      }
      setTimeout(restoreScrollPosition, 20, location.key)
    } catch (e) {
      actions.error.writeError({error: e})
    }
  }

  useEffect(() => {
    // Reset state when azubiId changes
    setAzubis(undefined)
    setVisibleAzubis([])
    setHasAzubisWithModule(true)
  }, [azubiId])

  useEffect(() => {
    fetchNachweise()
  }, [azubiId])

  const addDetailsLink = !azubiId

  const scroll = (skip, take) => {
    if (filteredAzubis) {
      setVisibleAzubis(filteredAzubis.slice(skip, skip + take))
    }
  }

  useEffect(() => {
    registerScrollRestoration(location.key)
  })

  useEffect(() => {
    return () => unregisterScrollRestoration(location.key)
  })

  const azubiIds = filteredAzubis ? filteredAzubis.map(azubi => azubi.userId) : []

  if (azubiId) {
    const azubi = azubis?.find(a => a.userId === azubiId)

    return (
      <div className="nachweisliste nachweisliste--verifier" data-testid="nachweisliste-ausbilder">
        <NachweisListeHeaderAusbilder />
        {azubi === undefined && <Loading />}
        {azubis && azubi && (
          <NachweisListeAusbilderPerAzubi
            key={azubi.userId}
            userId={azubi.userId}
            firstname={azubi.firstname}
            lastname={azubi.lastname}
            ausbildungsberufString={azubi.ausbildungsberufString}
            addDetailsLink={addDetailsLink}
            ausbildungsnachweise={toolAusbildungsNachweisSort.ausbildungsNachweisSort(
              azubi.ausbildungsnachweise.slice(),
              true,
            )}
            maxAusbildungsnachweisNummer={azubi.maxAusbildungsnachweisNummer}
          />
        )}
      </div>
    )
  }

  return (
    <div className="nachweisliste nachweisliste--verifier" data-testid="nachweisliste-ausbilder">
      <NachweisListeHeaderAusbilder />
      {filteredAzubis === undefined && <Loading />}
      {filteredAzubis && filteredAzubis.length === 0 && hasAzubisWithModule && (
        <Text text={i18n.t('no_azubi_with_fachabteilung_filter_visible')} />
      )}
      {filteredAzubis && filteredAzubis.length === 0 && !hasAzubisWithModule && (
        <Text text={i18n.t('no_azubi_with_this_module')} />
      )}
      {filteredAzubis && <Pagination data={azubiIds} scrollCallback={scroll} />}
      {filteredAzubis &&
        visibleAzubis.map(visibleAzubi => {
          const azubi = azubis?.find(a => a.userId === visibleAzubi.userId)
          if (azubi) {
            const submittedNachweise = azubi.ausbildungsnachweise.slice()
            return (
              <NachweisListeAusbilderPerAzubi
                key={azubi.userId}
                userId={azubi.userId}
                firstname={azubi.firstname}
                lastname={azubi.lastname}
                ausbildungsberufString={azubi.ausbildungsberufString}
                addDetailsLink={addDetailsLink}
                ausbildungsnachweise={submittedNachweise}
                maxAusbildungsnachweisNummer={azubi.maxAusbildungsnachweisNummer}
                nachweisStatus={azubi.nachweisStatus}
              />
            )
          }
        })}
    </div>
  )
}

NachweisListeAusbilder.propTypes = {
  azubiId: PropTypes.number,
}

export default NachweisListeAusbilder
